/**
 * Presentational pagination chrome, extracted verbatim from
 * DataTablePagination (Unit 6, insurescope-7149k.2.3.4; contract: the
 * 6.DESIGN brief on insurescope-7149k.2.3.1). The visual is FROZEN —
 * same DOM, same classes as the pre-extraction adapter output. Table
 * consumers keep using DataTablePagination; table-free consumers (the
 * client Conversations tab) drive this core directly with server Page
 * fields. The `total > 10` conversations threshold deliberately does
 * NOT live here: DocumentList renders this chrome at any total.
 */
import {
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

export interface PaginationToolbarProps {
    total: number;
    /** 1-based current page (adapters convert TanStack's 0-based pageIndex). */
    page: number;
    pages: number;
    pageSize: number;
    canPreviousPage: boolean;
    canNextPage: boolean;
    /** 1-based target page; one handler serves first/prev/next/last. */
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    entityLabel?: string;
    /** When > 0, the range line swaps for the selected-count line. */
    selectedCount?: number;
    /** Denominator of the selected-count line. */
    selectedOf?: number;
    /** Hides the rows-per-page select for a fixed-size consumer. */
    showPageSize?: boolean;
    /** Keeps the page buttons focusable once they are disabled. */
    focusableWhenDisabled?: boolean;
}

function formatPageRange(
    page: number,
    pageSize: number,
    total: number,
    label: string
): string | null {
    if (total <= 0) return null;
    const start = (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, total);
    return `Showing ${start}–${end} of ${total} ${label}`;
}

export function PaginationToolbar({
    total,
    page,
    pages,
    pageSize,
    canPreviousPage,
    canNextPage,
    onPageChange,
    onPageSizeChange,
    entityLabel = "rows",
    selectedCount = 0,
    selectedOf = 0,
    showPageSize = true,
    focusableWhenDisabled = false,
}: PaginationToolbarProps) {
    return (
        // `@container` makes the pagination bar query its OWN width rather
        // than the viewport. That matters when the bar's available width
        // shrinks for reasons other than a narrow viewport, e.g. the
        // Documents library opening a wide detail panel beside the table.
        // With viewport-based breakpoints, `sm:` / `lg:` stayed on at
        // desktop viewport even after the list column collapsed to ~600px,
        // and the bar's controls wrapped awkwardly. Container queries fix
        // that. The thresholds match Tailwind's default `sm` / `lg` values,
        // so a bare top-level usage (no detail panel) behaves the same as
        // before. `flex-wrap` still lets the right-side controls drop to a
        // second row when even the compact form is too wide.
        <div className="@container flex flex-wrap items-center justify-between gap-y-2 px-2">
            <div className="text-muted-foreground text-sm">
                {selectedCount > 0 ? (
                    <>
                        {selectedCount} of {selectedOf} row(s) selected.
                    </>
                ) : (
                    formatPageRange(page, pageSize, total, entityLabel)
                )}
            </div>
            <div className="flex items-center gap-3 @lg:gap-6 @2xl:gap-8">
                {showPageSize && (
                    <div className="flex items-center gap-2">
                        <p className="hidden text-sm font-medium @xl:block">
                            Rows per page
                        </p>
                        <Select
                            value={`${pageSize}`}
                            onValueChange={(value) => {
                                onPageSizeChange(Number(value));
                            }}
                        >
                            {/* The visible "Rows per page" text is display-only
                            (hidden below @xl, not programmatically
                            associated); the trigger needs its own name. */}
                            <SelectTrigger
                                aria-label="Rows per page"
                                className="h-8 w-[70px]"
                            >
                                <SelectValue placeholder={pageSize} />
                            </SelectTrigger>
                            <SelectContent side="top">
                                {[10, 20, 25, 30, 40, 50].map((sizeOption) => (
                                    <SelectItem
                                        key={sizeOption}
                                        value={`${sizeOption}`}
                                    >
                                        {sizeOption}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}
                <div className="hidden items-center justify-center text-sm font-medium whitespace-nowrap @lg:flex">
                    Page {page} of {pages}
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="icon"
                        className="hidden size-8 @2xl:flex"
                        onClick={() => onPageChange(1)}
                        disabled={!canPreviousPage}
                        focusableWhenDisabled={focusableWhenDisabled}
                    >
                        <span className="sr-only">Go to first page</span>
                        <ChevronsLeft />
                    </Button>
                    <Button
                        variant="outline"
                        size="icon"
                        className="size-8"
                        onClick={() => onPageChange(page - 1)}
                        disabled={!canPreviousPage}
                        focusableWhenDisabled={focusableWhenDisabled}
                    >
                        <span className="sr-only">Go to previous page</span>
                        <ChevronLeft />
                    </Button>
                    <Button
                        variant="outline"
                        size="icon"
                        className="size-8"
                        onClick={() => onPageChange(page + 1)}
                        disabled={!canNextPage}
                        focusableWhenDisabled={focusableWhenDisabled}
                    >
                        <span className="sr-only">Go to next page</span>
                        <ChevronRight />
                    </Button>
                    <Button
                        variant="outline"
                        size="icon"
                        className="hidden size-8 @2xl:flex"
                        onClick={() => onPageChange(pages)}
                        disabled={!canNextPage}
                        focusableWhenDisabled={focusableWhenDisabled}
                    >
                        <span className="sr-only">Go to last page</span>
                        <ChevronsRight />
                    </Button>
                </div>
            </div>
        </div>
    );
}
